import {useMemo, useContext, useState, useEffect} from 'react';
import {AppContext} from '../context/AppContext';
import {DescriptionHistoryService} from '../services/DescriptionHistoryService';

export interface DescriptionSuggestion {
  description: string;
  usageCount: number;
  source: 'history' | 'worklog';
}

export interface DescriptionSuggestions {
  recent: DescriptionSuggestion[];
  others: DescriptionSuggestion[];
}

/**
 * Hook to provide description autocomplete suggestions split into:
 * - recent: up to 3 most recently used (matching keyword)
 * - others: remaining suggestions to fill up to total 10
 */
export function useDescriptionAutocomplete(keyword: string): DescriptionSuggestions {
  const context = useContext(AppContext);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  if (!context) {
    throw new Error('useDescriptionAutocomplete must be used within AppContextProvider');
  }

  const {store} = context;

  // Refresh suggestions periodically to reflect worklog changes
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTrigger(prev => prev + 1);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const value = useMemo<DescriptionSuggestions>(() => {
    // Recent (by lastUsed desc), filtered by keyword, limited to 3
    const recentEntries = DescriptionHistoryService.getRecent(3, keyword);
    const recent: DescriptionSuggestion[] = recentEntries.map(e => ({
      description: e.description,
      usageCount: e.usageCount,
      source: 'history'
    }));

    // Build combined suggestions (history weighted + current worklogs) like before
    const historyEntries = DescriptionHistoryService.search(keyword);
    const worklogs = store.listSorted();
    const worklogDescMap = new Map<string, number>();

    worklogs.forEach(w => {
      const desc = w.description?.trim();
      if (desc && (!keyword || desc.toLowerCase().includes(keyword.toLowerCase()))) {
        worklogDescMap.set(desc, (worklogDescMap.get(desc) || 0) + 1);
      }
    });

    const combinedMap = new Map<string, DescriptionSuggestion>();

    // Add history entries
    historyEntries.forEach(entry => {
      combinedMap.set(entry.description, {
        description: entry.description,
        usageCount: entry.usageCount,
        source: 'history'
      });
    });

    // Merge worklog descriptions
    worklogDescMap.forEach((count, desc) => {
      const existing = combinedMap.get(desc);
      if (existing) {
        existing.usageCount += count;
      } else {
        combinedMap.set(desc, {
          description: desc,
          usageCount: count,
          source: 'worklog'
        });
      }
    });

    // Remove recent items from others
    const recentSet = new Set(recent.map(r => r.description));
    const othersAll = Array.from(combinedMap.values()).filter(s => !recentSet.has(s.description));

    // Sort others by priority (usage desc, then alpha)
    othersAll.sort((a, b) => {
      if (b.usageCount !== a.usageCount) return b.usageCount - a.usageCount;
      return a.description.localeCompare(b.description);
    });

    // Enforce total limit of 10 (recent + others)
    const remaining = Math.max(0, 10 - recent.length);
    const others = othersAll.slice(0, remaining);

    return { recent, others };
  }, [keyword, store, refreshTrigger]);

  return value;
}
